import { useEffect, useRef } from 'react';
import type { Tracker } from '../..';
import {
    createTracker,
    createArchiproTracker,
    createArchiproTrackerV2,
    createArchiproImpressionTracker,
    useLocationState,
    queueTracker,
    createGA4Tracker,
    createGoogleAdsTracker,
} from '../..';
import type { GoogleAdsConversionLabels } from '../../trackers/google-tracker/googleAdsTracker';
import { createFacebookTracker } from '../../trackers/facebook-tracker/facebookTracker';
import { createOpenAiTracker } from '../../trackers/openai-tracker/openAiTracker';
import { createHubspotTracker } from '../../trackers/hubspot-tracker/hubspotTracker';
import { isHubspotStaffUser } from '../../trackers/hubspot-tracker/isArchiproStaffEmail';
import type { Location } from 'history';
import { proxyTracker } from '../../trackers/tracker-proxy';
import { useBeforeUnload } from 'react-router';
import { createPinterestTracker } from '../../trackers/pinterest-tracker/pinterestTracker';
import { createOutbrainTracker } from '../../trackers/outbrain-tracker/outbrainTracker';

interface TrackerEnv {
    ga4TrackingId: string;
    /**
     * Google Ads conversion actions, keyed by conversion name. Absent or empty
     * means no Google Ads conversions are reported from the browser.
     */
    googleAdsConversionLabels?: GoogleAdsConversionLabels;
}

type ExtractUserType<T extends (user: never, ...args: never) => Tracker> =
    Parameters<T>[0];

type User = ExtractUserType<typeof createGA4Tracker> &
    ExtractUserType<typeof createArchiproImpressionTracker> &
    ExtractUserType<typeof createHubspotTracker>;

type TrackingUserIdentity = {
    ID: number;
    __typename: 'Me' | 'Guest';
    Email?: string;
    IsArchiproStaff?: boolean;
};

/**
 * Stable identity for deciding when trackers must be rebuilt after login/logout
 * without a full document reload (e.g. staff status for HubSpot).
 */
export function getTrackingUserKey(user: TrackingUserIdentity): string {
    if (user.__typename === 'Me') {
        return `Me:${user.ID}:${isHubspotStaffUser(user) ? '1' : '0'}`;
    }

    return `Guest:${user.ID}`;
}

function createArchiproTrackers(
    user: User,
    trackingEnv: TrackerEnv,
): Tracker[] {
    return [
        createArchiproTracker(),
        createArchiproTrackerV2(),
        createArchiproImpressionTracker(user),
        createGA4Tracker(user, trackingEnv.ga4TrackingId),
        createGoogleAdsTracker(trackingEnv.googleAdsConversionLabels),
        createFacebookTracker(),
        createOpenAiTracker(),
        createPinterestTracker(),
        createOutbrainTracker(),
        createHubspotTracker(user),
    ];
}

/**
 * This is the main entry point to all our tracking needs on both applications.
 * The applications themselves should not be using any of the trackers directly.
 *
 * The tracker waits for `user` to be available to setup the trackers.
 * If the user identity or staff flag changes later (SPA login without reload),
 * trackers are released and recreated so HubSpot can call `doNotTrack`.
 */
export const useArchiproTracking = (
    // Required because of conflicting react-router depenedencies.
    // It can be removed once both apps are on the same version of react-router
    // or we switch to pnmp
    location: Location,
    user: User | null,
    trackingEnv: TrackerEnv,
): Tracker => {
    const locationState = useLocationState(location);

    const eventCollection = useRef<Parameters<Tracker['log']>[] | undefined>(
        [],
    );

    // Disables trackers during SSR run
    // Make the tracker ref to avoid refreshing the state when we switch tracker instances.
    // Use queueTracker first to collect all events before the real tracker is ready.
    const trackerRef = useRef<Tracker>(queueTracker(eventCollection.current));

    // Use a proxy ref to work with `const { log } = useTracker();`
    // Destructuring an object would lost the `this` context.
    // So we need to use a function closure to keep the context here.
    const proxyRef = useRef<Tracker>(proxyTracker(trackerRef));

    const setupUserKeyRef = useRef<string | null>(null);

    // We have some pages are sitting on the old website. We would refresh the whole page when we visit those pages.
    // Some trackers might queue events and send them later. We would lost all events in the queue when we refresh the page.
    // So we need to notify trackers to flush the queue before the page is unloaded.
    useBeforeUnload(() => {
        if (trackerRef.current) {
            trackerRef.current.release();
        }
    });

    useEffect(() => {
        if (!user) return;

        const userKey = getTrackingUserKey(user);
        const isFirstSetup = typeof eventCollection.current !== 'undefined';

        if (!isFirstSetup && setupUserKeyRef.current === userKey) {
            return;
        }

        if (!isFirstSetup) {
            trackerRef.current.release();
        }

        const tracker = createTracker(
            createArchiproTrackers(user, trackingEnv),
            locationState.current,
        );

        trackerRef.current = tracker;
        setupUserKeyRef.current = userKey;

        if (isFirstSetup) {
            // Fire all the events that were pushed in before the trackers were ready
            eventCollection.current?.forEach((event) => {
                void tracker.log(...event);
            });
            // Don't want to hold it in memory
            // There's no use for them after they're fired
            eventCollection.current = undefined;
        }
        // trackingEnv / locationState are stable enough for tracker setup; user drives rebuilds.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    return proxyRef.current;
};
